/**
 * AgeGate
 *
 * Single-screen wrapper around Yoti's Web FCM. Embedded inside the
 * mobile-app's React Native WebView. On a successful capture, posts the
 * `{ img, secure: { version, token, signature, verification } }` envelope
 * back to RN via window.ReactNativeWebView.postMessage. On error, posts
 * a typed error code.
 *
 * Standalone browser testing: shows a confirmation overlay when the
 * envelope is captured. In RN-WebView usage the host navigates away on
 * postMessage before that overlay is ever rendered.
 */

import { useEffect, useRef, useState } from 'react';
import FaceCapture, {
  type FCMPayload,
  type Secure,
  ERROR_CODE,
} from '@getyoti/react-face-capture';

/**
 * Same-origin Firebase Hosting rewrite to the `validateAgeGateSession`
 * callable. Defined in `firebase.json` — avoids CORS and means the webapp
 * doesn't need to know which Firebase project hosts it (dev vs prod).
 *
 * Wire format follows Firebase callables: POST `{ data: {...} }`, response
 * `{ result: {...} }`. Since our backend wraps responses in `ApiResponse<T>`,
 * `result.data` is the actual payload.
 */
const VALIDATE_SESSION_ENDPOINT = '/__age-gate/validate-session';

// Public identifier — paired with our private PEM stored server-side.
// Not a secret; same value lives in mobile-app's google-services.json
// for other Yoti products.
const YOTI_CLIENT_SDK_ID = 'f0825907-8b13-401a-948f-0d5d85c837fb';

type RNMessage =
  | { type: 'envelope'; payload: { img: string; secure: Secure } }
  | { type: 'error'; code: ERROR_CODE | 'SECURE_FIELD_MISSING' }
  | { type: 'ready' }
  | { type: 'progress' };

/**
 * Heartbeat interval while Yoti is actively processing.
 *
 * The mobile-app runs a 90s silence timer that fires `fcm_failure` when
 * the WebView produces no postMessage for that long. Yoti's secure-mode
 * signing phase (between onReadyForCapture and onSuccess) is silent —
 * no callbacks fire during the 5-25s of on-device SICAP work, which on
 * slow networks/devices can exceed the silence threshold and produce a
 * spurious failure even though Yoti is still working correctly.
 *
 * Heartbeats every 10s keep the timer healthy; the mobile-app treats
 * `{type: 'progress'}` as a no-op that resets its silence counter.
 */
const HEARTBEAT_INTERVAL_MS = 10_000;

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (data: string) => void };
  }
}

function post(msg: RNMessage): void {
  const serialised = JSON.stringify(msg);
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(serialised);
  } else {
    // Standalone browser testing.
    // eslint-disable-next-line no-console
    console.log('[AgeGate]', serialised);
  }
}

type State =
  /**
   * Initial state — we're calling validateAgeGateSession to verify the
   * `?t=` query param. FaceCapture is NOT mounted yet, so even an unauth'd
   * visitor can't consume our Yoti SDK-ID capture-session allowance just
   * by loading the page.
   */
  | { kind: 'gating' }
  /** Token missing/invalid/expired — render an error, never mount Yoti. */
  | { kind: 'gate-failed'; reason: 'missing' | 'invalid' | 'network' }
  | { kind: 'capturing' }
  | { kind: 'done'; envelopeKeys: string[]; imgLength: number }
  | { kind: 'error'; code: string };

/**
 * Validate the `?t=` token against the backend. Returns true iff the
 * backend reports `valid: true`. Any non-2xx, network failure, or
 * unexpected shape is treated as a hard fail — we'd rather error-out a
 * legitimate user with a transient network blip than let a forged token
 * slip through (the user can retry by re-opening from the app).
 *
 * Result is cached at the call site for the page session so we don't
 * re-validate on every re-render.
 */
async function validateToken(token: string): Promise<{ ok: true; uid: string } | { ok: false; reason: 'invalid' | 'network' }> {
  let response: Response;
  try {
    response = await fetch(VALIDATE_SESSION_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: { token } }),
    });
  } catch {
    return { ok: false, reason: 'network' };
  }
  if (!response.ok) {
    return { ok: false, reason: 'network' };
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { ok: false, reason: 'network' };
  }
  // Callable wire format: { result: <handler return value> }.
  // Handler returns an ApiResponse: { success, data: { valid, uid?, expiresAt? }, metadata }.
  const result = (body as { result?: { data?: { valid?: boolean; uid?: string } } }).result;
  if (!result?.data || result.data.valid !== true || typeof result.data.uid !== 'string') {
    return { ok: false, reason: 'invalid' };
  }
  return { ok: true, uid: result.data.uid };
}

// Brand-aligned styling — keeps in sync with mobile-app/src/theme.
// Heading font intentionally uses Georgia as a system-available stand-in
// for Playfair Display, which the mobile app uses. Loading the actual
// webfont would add a render-blocking request on top of an already-heavy
// page; Georgia is a close visual match.
const HEADING_FONT = "Georgia, 'Times New Roman', serif";
const BODY_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

// Full-screen overlay with safe-area padding for notches / home bars.
// `calc(... + env(...))` keeps the visual padding the same on devices
// without insets and adds the system-reserved area on devices with them.
const fullScreenStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2147483646,
  paddingTop: 'calc(24px + env(safe-area-inset-top, 0px))',
  paddingRight: 'calc(24px + env(safe-area-inset-right, 0px))',
  paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
  paddingLeft: 'calc(24px + env(safe-area-inset-left, 0px))',
  textAlign: 'center',
  fontFamily: BODY_FONT,
  // Allow internal scrolling on very small viewports rather than clipping.
  overflowY: 'auto',
};

const successStyle: React.CSSProperties = {
  ...fullScreenStyle,
  // Mirrors mobile-app/src/theme/colors.ts gradients.primary (orange → accent → pink).
  background: 'linear-gradient(135deg, #F77630 0%, #FA9B0C 50%, #FC9FCA 100%)',
  color: '#FFFFFF',
};

const errorStyle: React.CSSProperties = {
  ...fullScreenStyle,
  background: '#FFFFFF',
  color: '#2C2C2C',
};

// Headings use `clamp()` so they scale down on iPhone SE-class screens
// (320px wide) without media queries.
const headingStyle: React.CSSProperties = {
  fontFamily: HEADING_FONT,
  fontWeight: 700,
  fontSize: 'clamp(22px, 6vw, 28px)',
  lineHeight: 1.2,
  letterSpacing: -0.5,
  margin: 0,
  // text-wrap: balance gives more even multi-line headings.
  textWrap: 'balance' as React.CSSProperties['textWrap'],
};

const bodyStyle: React.CSSProperties = {
  fontFamily: BODY_FONT,
  fontWeight: 400,
  fontSize: 'clamp(14px, 4vw, 16px)',
  lineHeight: 1.5,
  marginTop: 12,
  opacity: 0.9,
  maxWidth: 360,
};

const captionStyle: React.CSSProperties = {
  fontFamily: BODY_FONT,
  fontWeight: 400,
  fontSize: 12,
  lineHeight: 1.5,
  marginTop: 24,
  opacity: 0.7,
  maxWidth: 320,
  wordBreak: 'break-word',
};

const iconCircleStyle: React.CSSProperties = {
  width: 'clamp(64px, 18vw, 80px)',
  height: 'clamp(64px, 18vw, 80px)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'clamp(32px, 9vw, 40px)',
  marginBottom: 20,
  // Hint for the GPU — avoids subpixel shimmer on scale.
  willChange: 'transform',
};

/**
 * Stable viewport-sized parent for the Yoti SDK. The SDK measures its
 * own layout off the parent box on mount; without an explicit fixed
 * container its overlay can render outside the visible viewport on iOS
 * WebView (observed: instructional panel rendered at the bottom-right
 * corner clipped off-screen). Pinning to `inset: 0` gives the SDK a
 * deterministic box to size against.
 */
const faceCaptureContainerStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  overflow: 'hidden',
};

/**
 * Loading copy rotated through the preparing screen. Kept in lockstep
 * with the inline rotator in index.html. The shared `performance.now()`
 * clock (== ms since navigation start) means the static skeleton and
 * the React overlay continue the same sequence without resetting when
 * React mounts.
 */
const LOADING_MESSAGES = [
  'Preparing verification',
  'Connecting securely',
  'Loading camera',
  'Almost there',
];

function getLoadingMessage(): string {
  const i = Math.min(
    Math.floor(performance.now() / 2000),
    LOADING_MESSAGES.length - 1,
  );
  return LOADING_MESSAGES[i];
}

function useLoadingMessage(): string {
  const [msg, setMsg] = useState(getLoadingMessage);
  useEffect(() => {
    const id = setInterval(() => setMsg(getLoadingMessage()), 500);
    return () => clearInterval(id);
  }, []);
  return msg;
}

function PreparingScreen({ overlay = false }: { overlay?: boolean }) {
  const msg = useLoadingMessage();
  // Overlay variant sits on top of <FaceCapture> while Yoti boots;
  // z-index is one below React's max so any modal we add later still
  // wins.
  const containerStyle = overlay
    ? { ...errorStyle, zIndex: 2147483647 }
    : errorStyle;
  return (
    <div style={containerStyle}>
      <div className="age-gate-spinner" />
      <h1 style={headingStyle}>{msg}</h1>
      <p style={bodyStyle}>One moment.</p>
    </div>
  );
}

export function AgeGate() {
  const [state, setState] = useState<State>({ kind: 'gating' });
  /**
   * Defers the Yoti SDK mount by two animation frames after entering
   * the `capturing` state. On iOS WebView, `window.innerWidth/Height`
   * can still be transitioning during the status-bar / URL-bar collapse
   * at first paint; mounting the SDK before that settles caused its
   * overlay to size against a phantom viewport and render off-screen.
   */
  const [mountReady, setMountReady] = useState(false);
  /**
   * Whether Yoti's camera stream is actually live (onReadyForCapture
   * has fired). We keep the static "Preparing verification" screen
   * rendered as an overlay on top of <FaceCapture> until this flips,
   * so users see ONE continuous loading state instead of three
   * (ours → blank container while Yoti mounts → Yoti's own loader).
   */
  const [sdkReady, setSdkReady] = useState(false);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // useRef guard so React 18 StrictMode's double-mount in dev doesn't fire
  // the validation request twice and waste a function invocation.
  const gateStartedRef = useRef(false);

  useEffect(() => {
    if (!gateStartedRef.current) {
      gateStartedRef.current = true;
      const token = new URLSearchParams(window.location.search).get('t');
      if (!token) {
        setState({ kind: 'gate-failed', reason: 'missing' });
      } else {
        void validateToken(token).then((res) => {
          if (res.ok) {
            setState({ kind: 'capturing' });
          } else {
            setState({ kind: 'gate-failed', reason: res.reason });
          }
        });
      }
    }

    // Always clear the heartbeat on unmount — guards against leaks if
    // RN tears the WebView down mid-flow (navigation, screen change).
    return () => stopHeartbeat();
  }, []);

  /**
   * Wait for the iOS WebView viewport to actually stabilise before
   * mounting Yoti. Two rAFs (~32ms) wasn't enough in practice — the SDK
   * was still mounting before the status-bar / URL-bar animation
   * settled, then reflowing "a couple of seconds later" once iOS fired
   * its own resize. We instead poll visualViewport.height (more
   * accurate than window.innerHeight on iOS) once per rAF and only
   * mount after STABLE_FRAMES consecutive identical readings, with a
   * hard cap so we never hang.
   *
   * This runs on first mount — in PARALLEL with the `validateToken`
   * network round-trip — rather than waiting for the `capturing` state.
   * The viewport settle (a local rAF loop) and the token validation
   * (a network call) are independent, so overlapping them removes the
   * settle window from the user-visible "Preparing" time: by the time
   * the token returns, `mountReady` is usually already true. FaceCapture
   * still only renders once state === 'capturing' AND mountReady, so we
   * never mount Yoti for an unvalidated session.
   */
  useEffect(() => {
    if (mountReady) return;
    const STABLE_FRAMES = 3;
    const MAX_WAIT_MS = 500;
    const start = performance.now();
    let lastHeight = -1;
    let stableCount = 0;
    let rafId = 0;
    const readHeight = (): number =>
      window.visualViewport?.height ?? window.innerHeight;
    const tick = (): void => {
      const h = readHeight();
      if (h === lastHeight) {
        stableCount += 1;
      } else {
        stableCount = 1;
        lastHeight = h;
      }
      if (stableCount >= STABLE_FRAMES || performance.now() - start >= MAX_WAIT_MS) {
        setMountReady(true);
        return;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [mountReady]);

  /**
   * After mount, if the visualViewport changes (URL bar collapse,
   * keyboard close, camera permission dismiss), force the SDK to
   * re-measure by dispatching a synthetic window resize. The Yoti SDK
   * subscribes to window resize internally; this is a no-op when the
   * SDK has already measured the correct viewport.
   */
  useEffect(() => {
    if (!mountReady) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = (): void => {
      window.dispatchEvent(new Event('resize'));
    };
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, [mountReady]);

  function startHeartbeat(): void {
    if (heartbeatRef.current !== null) return;
    heartbeatRef.current = setInterval(() => {
      post({ type: 'progress' });
    }, HEARTBEAT_INTERVAL_MS);
  }

  function stopHeartbeat(): void {
    if (heartbeatRef.current !== null) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }

  function handleSuccess(payload: FCMPayload): void {
    stopHeartbeat();
    if (!payload.secure) {
      post({ type: 'error', code: 'SECURE_FIELD_MISSING' });
      setState({ kind: 'error', code: 'SECURE_FIELD_MISSING' });
      return;
    }
    post({
      type: 'envelope',
      payload: { img: payload.img, secure: payload.secure },
    });
    setState({
      kind: 'done',
      envelopeKeys: Object.keys(payload.secure),
      imgLength: payload.img.length,
    });
  }

  if (state.kind === 'gating') {
    return <PreparingScreen />;
  }

  if (state.kind === 'gate-failed') {
    return (
      <div style={errorStyle}>
        <div style={{ ...iconCircleStyle, background: '#FEE2E2', color: '#EF4444' }}>!</div>
        <h1 style={headingStyle}>This page can't be opened directly</h1>
        <p style={bodyStyle}>This page must be opened from the EVE app. Please return to the app and try again.</p>
        <div style={{ ...captionStyle, color: '#979797' }}>{`gate_${state.reason}`}</div>
      </div>
    );
  }

  if (state.kind === 'done') {
    return (
      <div style={successStyle}>
        <div style={{ ...iconCircleStyle, background: 'rgba(255,255,255,0.18)' }}>✓</div>
        <h1 style={headingStyle}>You're all set</h1>
        <p style={bodyStyle}>Age verification complete.</p>
        <div style={captionStyle}>
          {state.imgLength.toLocaleString()} bytes · {state.envelopeKeys.join(' · ')}
        </div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div style={errorStyle}>
        <div style={{ ...iconCircleStyle, background: '#FEE2E2', color: '#EF4444' }}>!</div>
        <h1 style={headingStyle}>Something went wrong</h1>
        <p style={bodyStyle}>We couldn't complete the verification.</p>
        <div style={{ ...captionStyle, color: '#979797' }}>{state.code}</div>
      </div>
    );
  }

  if (!mountReady) {
    return <PreparingScreen />;
  }

  return (
    <div style={faceCaptureContainerStyle}>
      {!sdkReady && <PreparingScreen overlay />}
      <FaceCapture
        clientSdkId={YOTI_CLIENT_SDK_ID}
        secure={true}
        // multiframe intentionally OFF — it forces hd/high-quality images
        // that drive the slow on-device signing and is not an HEAA
        // requirement. MUST match the backend query param in
        // functions/src/services/yotiAgeClient.ts (no `multiframe=true`),
        // or Yoti rejects with UNTRUSTED_SECURE_SESSION. See
        // docs/plans/UK_YOTI_AGE_ESTIMATION.md §7 row 5.
        multiframe={false}
        // Skip Yoti's static instructional panel. Its Continue button is
        // visible but inert until Yoti finishes its secure-session +
        // capture-module round-trips to api.yoti.com (multi-second window
        // on cold network), which reads to users as a frozen UI. Without
        // the panel, we go straight to the camera view, and Yoti's live
        // face-overlay hints carry the guidance once frames are flowing.
        showInitialGuidance={false}
        manualCaptureFallback={true}
        returnPreviewImage={false}
        onReadyForCapture={() => {
          post({ type: 'ready' });
          // Yoti will go quiet between here and onSuccess while it signs
          // the captured frames. Keep mobile's silence timer alive.
          startHeartbeat();
          // Camera is live — let the overlay drop so users see the
          // capture view. Until this point the static loader has been
          // covering Yoti's own mount/init UI.
          setSdkReady(true);
        }}
        onSuccess={handleSuccess}
        onError={(code: ERROR_CODE) => {
          stopHeartbeat();
          post({ type: 'error', code });
          setState({ kind: 'error', code });
        }}
      />
    </div>
  );
}
